import React, { useEffect, useState } from "react";
import ActiveUsersChart from "./report-charts/ActiveUsersChart";
import PopularCategoriesChart from "./report-charts/PopularCategoriesChart";
import ProctorRequestsChart from "./report-charts/ProctorRequestsChart";
import TotalUsersChart from "./report-charts/TotalUsersChart";
import "./Reports.css";

import {
  fetchNewUsersReport,
  fetchCategoriesReport,
  fetchProctorReports,
  fetchTotalUsersReport,
} from "../../apis/reportsApis";

import ReusableLoader from "../../components/reusable-loader/ReusableLoader";
import { fetchLibrariesList } from "../../apis/libraryApis";

const getCurrentMonthRange = () => {
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
  const endDate = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0,
    23,
    59,
    59,
  );
  return { startDate, endDate };
};

const Reports: React.FC = () => {
  const [activeUsers, setActiveUsers] = useState<
    { name: string; value: number }[]
  >([]);
  const [practiceTest, setPracticeTest] = useState<any>(null);
  const [learningCourse, setLearningCourse] = useState<any>(null);
  const [proctorRequests, setProctorRequests] = useState<any[]>([]);
  const [totalUsers, setTotalUsers] = useState<any[]>([]);

  const [activeUsersDateRange, setActiveUsersDateRange] = useState(
    getCurrentMonthRange(),
  );

  const [proctorDateRange, setProctorDateRange] = useState(
    getCurrentMonthRange(),
  );
  const [categoriesDateRange, setCategoriesDateRange] = useState(
    getCurrentMonthRange(),
  );

  const [loadingActiveUsers, setLoadingActiveUsers] = useState(false);
  const [loadingTotalUsers, setLoadingTotalUsers] = useState(false);
  const [loadingProctorRequests, setLoadingProctorRequests] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [libraries, setLibraries] = useState<
    { label: string; value: string }[]
  >([]);
  const [selectedLibrary, setSelectedLibrary] = useState("all");

  // 🟤 Track if it’s the first render
  const [firstLoad, setFirstLoad] = useState(true);

  // 🟤 Fetch Active Users
  useEffect(() => {
    const fetchActiveUsers = async () => {
      setLoadingActiveUsers(true);
      try {
        const res = await fetchNewUsersReport(
          activeUsersDateRange.startDate.toISOString(),
          activeUsersDateRange.endDate.toISOString(),
        );
        const usersData =
          res?.data?.map((item: any) => ({
            name: item.name,
            value: Number(item.usercount),
          })) || [];
        setActiveUsers(usersData);
      } finally {
        setLoadingActiveUsers(false);
      }
    };
    fetchActiveUsers();
  }, [activeUsersDateRange]);

  // 🟤 Fetch Total Users
  useEffect(() => {
    const fetchTotalUsers = async () => {
      setLoadingTotalUsers(true);
      try {
        const res = await fetchTotalUsersReport();
        const totalData =
          res?.data?.map((item: any) => ({
            name: item.name,
            value: Number(item.usercount),
          })) || [];
        setTotalUsers(totalData);
      } finally {
        setLoadingTotalUsers(false);
      }
    };
    fetchTotalUsers();
  }, []);

  // 🟤 Fetch Proctor Requests
  useEffect(() => {
    const fetchProctorData = async () => {
      setLoadingProctorRequests(true);
      try {
        const res = await fetchProctorReports(
          proctorDateRange.startDate.toISOString(),
          proctorDateRange.endDate.toISOString(),
        );
        const formattedData =
          res?.data?.[0]?.libraryProctorStatus?.map((item: any) => {
            const statusCounts = item.proctorData?.reduce(
              (acc: any, curr: any) => {
                if (curr.proctorStatus === "approved") {
                  acc.approved += curr.proctorCount;
                } else if (curr.proctorStatus === "reject") {
                  acc.rejected += curr.proctorCount;
                } else if (curr.proctorStatus === "pending") {
                  acc.pending += curr.proctorCount;
                }
                return acc;
              },
              { approved: 0, rejected: 0, pending: 0 },
            ) || { approved: 0, rejected: 0, pending: 0 };

            return {
              name: item.name,
              approved: statusCounts.approved,
              rejected: statusCounts.rejected,
              pending: statusCounts.pending,
            };
          }) || [];
        setProctorRequests(formattedData);
      } finally {
        setLoadingProctorRequests(false);
      }
    };
    fetchProctorData();
  }, [proctorDateRange]);

  // 🟤 Fetch Categories
  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const res = await fetchCategoriesReport(
          categoriesDateRange.startDate.toISOString(),
          categoriesDateRange.endDate.toISOString(),
          selectedLibrary === "all" ? null : selectedLibrary,
        );

        const keySummary = res?.data?.[0]?.keyTypeSummary || [];

        // Reset both first (so old data doesn’t linger)
        setPracticeTest({
          categories: [],
          values: [],
          total: 0,
          title: "Practice Test",
        });
        setLearningCourse({
          categories: [],
          values: [],
          total: 0,
          title: "Learning Course",
        });

        keySummary.forEach((summary: any) => {
          const categories = summary.courses_details.map(
            (c: any) => c.course.courseName,
          );
          const values = summary.courses_details.map((c: any) => c.count);
          const total = values.reduce((a: number, b: number) => a + b, 0);

          if (summary.key_type_name.toLowerCase().includes("practice")) {
            setPracticeTest({
              categories,
              values,
              total,
              title: summary.key_type_name,
            });
          } else if (summary.key_type_name.toLowerCase().includes("learning")) {
            setLearningCourse({
              categories,
              values,
              total,
              title: summary.key_type_name,
            });
          }
        });
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, [categoriesDateRange, selectedLibrary]);

  // 🟤 Once everything is loaded first time → disable firstLoad
  useEffect(() => {
    if (
      !loadingActiveUsers &&
      !loadingTotalUsers &&
      !loadingProctorRequests &&
      !loadingCategories
    ) {
      setFirstLoad(false);
    }
  }, [
    loadingActiveUsers,
    loadingTotalUsers,
    loadingProctorRequests,
    loadingCategories,
  ]);

  useEffect(() => {
    const fetchLibraries = async () => {
      try {
        const res = await fetchLibrariesList();
        const formatted =
          res?.data?.map((lib: any) => ({
            label: lib.name,
            value: lib.id || null,
          })) || [];
        setLibraries([{ label: "All Libraries", value: "all" }, ...formatted]);
      } catch (err) {
        console.error("Error fetching libraries:", err);
      }
    };
    fetchLibraries();
  }, []);

  // 🟤 Show big loader only for first load
  if (firstLoad) {
    return <ReusableLoader />;
  }

  return (
    <div className="wsl-reports">
      <h2>View Reports</h2>

      <ActiveUsersChart
        data={activeUsers}
        setDateRange={setActiveUsersDateRange}
        loading={loadingActiveUsers}
      />

      <TotalUsersChart data={totalUsers} loading={loadingTotalUsers} />

      {practiceTest && learningCourse && (
        <PopularCategoriesChart
          practiceTest={practiceTest}
          learningCourse={learningCourse}
          setDateRange={setCategoriesDateRange}
          loading={loadingCategories}
          libraries={libraries}
          selectedLibrary={selectedLibrary}
          onLibraryChange={setSelectedLibrary}
        />
      )}

      <ProctorRequestsChart
        data={proctorRequests}
        setDateRange={setProctorDateRange}
        loading={loadingProctorRequests}
      />
    </div>
  );
};

export default Reports;
